import { ColumnType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router";
import { projectsApis } from "../../api/projects";
import dayjs from "dayjs";
import { TooltipComponent } from "../../components/Component";
import { useWbsName } from "../ProjectScheduleManagement/shared/useWbsCodes";
import { updatePhaseInDetailModalSlice } from "../../store/slices/projectManagement/phase-view/PhaseInDetailView";
import { Progress, Tooltip } from "antd";

const PhaseLevelPrograessSummaryReport = () => {
  const [statusFilterData, setStatusFilterData] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const wbsName = useWbsName();

  // Scroll to top when component mounts or when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const columns: ColumnType<any>[] = [
    {
      title: "Project Name",
      dataIndex: "projectName",
      key: "projectName",
      width: 300,
      fixed: "left",
      // render(text: any, record: any) {
      //     return (
      //         <>
      //             <span
      //                 className="inlineTableCellLink"
      //                 id={`project-${record?.projectId}`}
      //                 onClick={() => {
      //                     dispatch(
      //                         updateAllProjectsListViewType({
      //                             id: record.projectId,
      //                             onClose: "openProjectModal",
      //                             allowEdit: false,
      //                             open: true,
      //                         })
      //                     );
      //                     navigate(`/pms/project/view/`, {
      //                         state: { id: record.projectId, projectName: text }
      //                     });
      //                 }}
      //             >
      //                 {text}
      //             </span>
      //             <TooltipComponent
      //                 iconClass="info-circle text-info"
      //                 icon={""}
      //                 id={`task-${record?.taskId}`}
      //                 direction="right"
      //                 text={"Click To View " + text + " Details"}
      //                 containerClassName={"mt-1"}
      //             />
      //         </>
      //     );
      // },
    },
    {
      title: "Phase Name",
      dataIndex: "phaseName",
      key: "phaseName",
      width: 250,
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`phase-${record?.phaseId}`}
              onClick={() => {
                navigate("/pms/activity-level-progress-summary-report", {
                  state: {
                    ...location.state,
                    phaseIds: [record.phaseId],
                    phaseName: text,
                  },
                });
              }}
            >
              {wbsName(text)}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`phase-${record?.phaseId}`}
              direction="right"
              text={"Click To View Activities in " + wbsName(text)}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Planned Start",
      dataIndex: "plannedStartDate",
      key: "plannedStartDate",
      width: 100,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Planned End",
      dataIndex: "plannedEndDate",
      key: "plannedEndDate",
      width: 100,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Actual Start",
      dataIndex: "actualStartDate",
      key: "actualStartDate",
      width: 100,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Actual End",
      dataIndex: "actualEndDate",
      key: "actualEndDate",
      width: 100,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Planned Hours",
      dataIndex: "plannedHours",
      key: "plannedHours",
      width: 120,
      render: (text) => {
        if (text === null || text === undefined || text === "") {
          return "N/A";
        }
        return text;
      },
    },
    {
      title: "Logged Hours",
      dataIndex: "loggedHours",
      key: "loggedHours",
      width: 120,
      render: (text) => {
        if (text === null || text === undefined || text === "") {
          return "N/A";
        }
        return text;
      },
    },
    {
      title: "Completed Percentage",
      dataIndex: "activityCompletionPercentage",
      key: "activityCompletionPercentage",
      width: 200,
      render: (text, record) => {
        if (text === null || text === undefined || text === "") return "N/A";

        const percentage = parseFloat(text.toString().replace("%", ""));

        return (
          <Tooltip title={`Completion: ${text}`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percentage}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Budget Progress",
      dataIndex: "budgetConsumption",
      key: "budgetConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.budgetPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
  ];

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule("Phase");
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    fetchStatusFilterData();
    dispatch(
      updateMenuData([
        {
          text: "Phase Level Progress Summary Report",
          link: "/pms/phase-level-progress-summary-report",
        },
      ])
    );
  }, []);
  return (
    <>
      <Template1
        moduleKey={window.location.pathname}
        columns={columns}
        pageTitle="Phase Level Progress Summary Report"
        pageDescription=""
        previousPath={{
          link: "/pms/project/reports",
          state: {
            id: location.state?.id,
            projectName: location.state?.projectName,
            // Clear any drill-down state when going back to reports
          },
        }}
        // filters={{
        //   customFilters: [
        //     {
        //       type: "multiple_selection",
        //       name: "phaseStatus",
        //       label: "Status",
        //       options: statusFilterData,
        //     },
        //   ],
        // }}
      />
    </>
  );
};

export default PhaseLevelPrograessSummaryReport;
